import { LIST_USER_ANALYTICS_VIEWS_NAME } from "@/lib/ai/tools/list-user-analytics-views/constants";
import type { ListUaViewsOutput } from "@/lib/ai/tools/list-user-analytics-views/server";
import { defineDatasetSpec } from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildListTableDataset,
  createTableOnlyProfile,
  toListBoolean,
  toListNumber,
  toListText,
  toListTextList,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const UA_VIEWS_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_USER_ANALYTICS_VIEWS_NAME}-table`,
  profile: createTableOnlyProfile({ datasetFamily: "list.userAnalyticsViews" }),
  fields: [
    { key: "userAnalyticsId", role: "dim", type: "string" },
    { key: "viewId", role: "dim", type: "string" },
    { key: "viewName", role: "dim", type: "string" },
    { key: "description", role: "dim", type: "string" },
    { key: "visitedPage", role: "dim", type: "string" },
    { key: "visitedPageFilterType", role: "dim", type: "string" },
    { key: "userPseudoId", role: "dim", type: "string" },
    { key: "userPseudoIdFilterType", role: "dim", type: "string" },
    { key: "sessionId", role: "dim", type: "string" },
    { key: "sessionIdFilterType", role: "dim", type: "string" },
    { key: "firstPageLocationFilterType", role: "dim", type: "string" },
    { key: "firstPageLocationURL", role: "dim", type: "string" },
    {
      key: "firstPageLocationURLGroupCount",
      role: "metric",
      type: "number",
      unit: "count",
    },
    { key: "firstPageTitle", role: "dim", type: "string" },
    { key: "firstPageTitleFilterType", role: "dim", type: "string" },
    { key: "secondPageLocationFilterType", role: "dim", type: "string" },
    { key: "secondPageLocationURL", role: "dim", type: "string" },
    {
      key: "secondPageLocationURLGroupCount",
      role: "metric",
      type: "number",
      unit: "count",
    },
    { key: "secondPageTitle", role: "dim", type: "string" },
    { key: "secondPageTitleFilterType", role: "dim", type: "string" },
    { key: "os", role: "dim", type: "string" },
    { key: "devices", role: "dim", type: "string" },
    { key: "browsers", role: "dim", type: "string" },
    { key: "defaultChannelGroup", role: "dim", type: "string" },
    { key: "sources", role: "dim", type: "string" },
    { key: "mediums", role: "dim", type: "string" },
    { key: "terms", role: "dim", type: "string" },
    { key: "contents", role: "dim", type: "string" },
    { key: "campaigns", role: "dim", type: "string" },
    { key: "campaignsFilterType", role: "dim", type: "string" },
    { key: "events", role: "dim", type: "string" },
    { key: "eventsNotIn", role: "dim", type: "string" },
    { key: "expVariantString", role: "dim", type: "string" },
    { key: "eventsCount", role: "metric", type: "number", unit: "count" },
    {
      key: "eventsNotInCount",
      role: "metric",
      type: "number",
      unit: "count",
    },
    { key: "isSessionWithNavigation", role: "dim", type: "string" },
    { key: "pageLocationCountMin", role: "metric", type: "number" },
    { key: "pageLocationCountMax", role: "metric", type: "number" },
    { key: "gaSessionNumberMin", role: "metric", type: "number" },
    { key: "gaSessionNumberMax", role: "metric", type: "number" },
  ] as const,
  defaults: { category: "viewName", value: "eventsCount" },
  notes: "Table dataset normalized from list_user_analytics_views output.",
});

function toUaViewRows(output: Partial<ListUaViewsOutput>): VizDatasetRow[] {
  const views = Array.isArray(output.views) ? output.views : [];

  return views.map((view) => ({
    userAnalyticsId: toListText(output.userAnalyticsId),
    viewId: toListText(view.id),
    viewName: toListText(view.name),
    description: toListText(view.description),
    visitedPage: toListText(view.visitedPage),
    visitedPageFilterType: toListText(view.visitedPageFilterType),
    userPseudoId: toListText(view.userPseudoId),
    userPseudoIdFilterType: toListText(view.userPseudoIdFilterType),
    sessionId: toListText(view.sessionId),
    sessionIdFilterType: toListText(view.sessionIdFilterType),
    firstPageLocationFilterType: toListText(view.firstPageLocationFilterType),
    firstPageLocationURL: toListText(view.firstPageLocationURL),
    firstPageLocationURLGroupCount: toListNumber(
      Array.isArray(view.firstPageLocationURLGroupIDs)
        ? view.firstPageLocationURLGroupIDs.length
        : 0,
    ),
    firstPageTitle: toListText(view.firstPageTitle),
    firstPageTitleFilterType: toListText(view.firstPageTitleFilterType),
    secondPageLocationFilterType: toListText(view.secondPageLocationFilterType),
    secondPageLocationURL: toListText(view.secondPageLocationURL),
    secondPageLocationURLGroupCount: toListNumber(
      Array.isArray(view.secondPageLocationURLGroupIDs)
        ? view.secondPageLocationURLGroupIDs.length
        : 0,
    ),
    secondPageTitle: toListText(view.secondPageTitle),
    secondPageTitleFilterType: toListText(view.secondPageTitleFilterType),
    os: toListTextList(view.os),
    devices: toListTextList(view.devices),
    browsers: toListTextList(view.browsers),
    defaultChannelGroup: toListTextList(view.defaultChannelGroup),
    sources: toListTextList(view.sources),
    mediums: toListTextList(view.mediums),
    terms: toListTextList(view.terms),
    contents: toListTextList(view.contents),
    campaigns: toListTextList(view.campaigns),
    campaignsFilterType: toListTextList(view.campaignsFilterType),
    events: toListTextList(view.events),
    eventsNotIn: toListTextList(view.eventsNotIn),
    expVariantString: toListTextList(view.expVariantString),
    eventsCount: toListNumber(
      Array.isArray(view.events) ? view.events.length : 0,
    ),
    eventsNotInCount: toListNumber(
      Array.isArray(view.eventsNotIn) ? view.eventsNotIn.length : 0,
    ),
    isSessionWithNavigation: toListBoolean(view.isSessionWithNavigation),
    pageLocationCountMin: toListNumber(view.pageLocationCount?.min),
    pageLocationCountMax: toListNumber(view.pageLocationCount?.max),
    gaSessionNumberMin: toListNumber(view.gaSessionNumber?.min),
    gaSessionNumberMax: toListNumber(view.gaSessionNumber?.max),
  }));
}

/**
 * list_user_analytics_views 出力を table-only dataset へ正規化する。
 */
export function buildListUserAnalyticsViewsDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const typedOutput =
    typeof output === "object" && output !== null
      ? (output as Partial<ListUaViewsOutput>)
      : {};
  const rows = toUaViewRows(typedOutput);
  const dataset = buildListTableDataset({
    id: UA_VIEWS_DATASET_SPEC.id,
    datasetFamily: UA_VIEWS_DATASET_SPEC.profile.datasetFamily,
    variant: UA_VIEWS_DATASET_SPEC.profile.variant,
    source,
    rows,
    fields: UA_VIEWS_DATASET_SPEC.fields,
    defaults: UA_VIEWS_DATASET_SPEC.defaults,
    notes: UA_VIEWS_DATASET_SPEC.notes,
  });

  return dataset ? [dataset] : [];
}
